import CryptoJS from 'crypto-js';

/**
 * Retrieve and parse the base64-encoded key from process.env.
 */
const getEncryptionKey = () => {
  const rawKey = process.env.REACT_APP_PAYLOAD_ENCRYPTION_KEY;
  if (!rawKey) return null;
  try {
    return CryptoJS.enc.Base64.parse(rawKey);
  } catch (e) {
    console.error("Failed to parse REACT_APP_PAYLOAD_ENCRYPTION_KEY as Base64:", e);
    return null;
  }
};

/**
 * Returns a 16-byte zero-filled Initialization Vector (IV).
 * In CBC mode, this ensures consistent encryption/decryption for API payloads.
 */
const getIV = () => {
  return CryptoJS.enc.Hex.parse("00000000000000000000000000000000");
};

/**
 * Encrypt data using AES-256-CBC and return a Base64-encoded ciphertext.
 * Returns original data if the key is not configured.
 */
export const encryptPayload = (data) => {
  if (data === null || data === undefined) return data;
  
  const key = getEncryptionKey();
  if (!key) return data;

  try {
    const plaintext = typeof data === 'string' ? data : JSON.stringify(data);
    const iv = getIV();
    const encrypted = CryptoJS.AES.encrypt(plaintext, key, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7
    });
    return encrypted.toString(); // Base64 representation of ciphertext
  } catch (err) {
    console.error("Payload encryption error:", err);
    return data;
  }
};

/**
 * Decrypt ciphertext using AES-256-CBC.
 * Parses decrypted plaintext to JSON object if valid; otherwise returns raw string.
 * Returns null if decryption fails.
 */
export const decryptPayload = (ciphertext) => {
  if (!ciphertext || typeof ciphertext !== 'string') return null;

  const key = getEncryptionKey();
  if (!key) return null;

  try {
    const iv = getIV();
    const decrypted = CryptoJS.AES.decrypt(ciphertext, key, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7
    });
    const plaintext = decrypted.toString(CryptoJS.enc.Utf8);
    if (!plaintext) return null;
    try {
      return JSON.parse(plaintext);
    } catch {
      return plaintext;
    }
  } catch (err) {
    console.error("Payload decryption error:", err);
    return null;
  }
};

/**
 * Helper to automatically detect and decrypt encrypted data in response bodies.
 * Supports:
 * 1. String: Entire response body is encrypted.
 * 2. Object with 'payload' string field: Only the 'payload' property is encrypted.
 */
export const decryptIfNeeded = (data) => {
  if (!data) return data;

  const keyStr = process.env.REACT_APP_PAYLOAD_ENCRYPTION_KEY;
  if (!keyStr) return data;

  // Case 1: Entire data is the encrypted string
  if (typeof data === 'string') {
    const decrypted = decryptPayload(data);
    if (decrypted !== null) return decrypted;
  }

  // Case 2: Response data is an object containing an encrypted string in the 'payload' or 'data' property
  if (data && typeof data === 'object') {
    if (typeof data.payload === 'string') {
      const decrypted = decryptPayload(data.payload);
      if (decrypted !== null) {
        return {
          ...data,
          payload: decrypted
        };
      }
    }
    if (typeof data.data === 'string') {
      const decrypted = decryptPayload(data.data);
      if (decrypted !== null) {
        return {
          ...data,
          data: decrypted
        };
      }
    }
  }

  return data;
};
